<template>
  <div class="admin-lead-forms-index p-4 sm:p-6 lg:p-8">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">Lead Forms</h1>
        <p class="text-gray-600 mt-1">Manage all lead capture forms created by users</p>
      </div>
    </div>

    <!-- Forms Table -->
    <div class="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Slug</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Views</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submissions</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conversion</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="form in forms.data" :key="form.id" class="hover:bg-gray-50 transition-colors">
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {{ form.title }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                <code class="px-2 py-1 bg-gray-100 rounded text-xs">{{ form.slug }}</code>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  :class="[
                    'px-2 py-1 text-xs font-medium rounded-full',
                    form.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  ]"
                >
                  {{ form.is_active ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                {{ form.views || 0 }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                {{ form.submissions || 0 }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                <span class="font-medium">{{ form.conversion_rate || 0 }}%</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                <span v-if="form.user">{{ form.user.name }}</span>
                <span v-else class="text-gray-400">Guest</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <div class="flex items-center gap-2">
                  <Link
                    :href="route('admin.lead-forms.show', form.id)"
                    class="text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    View
                  </Link>
                  <Link
                    :href="route('admin.lead-forms.submissions', form.id)"
                    class="text-green-600 hover:text-green-800 hover:underline"
                  >
                    Submissions
                  </Link>
                  <button
                    @click="deleteForm(form.id)"
                    class="text-red-600 hover:text-red-800 hover:underline"
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="forms.data.length === 0">
              <td colspan="8" class="px-6 py-12 text-center">
                <div class="flex flex-col items-center gap-3">
                  <icon name="contact" class="w-16 h-16 text-gray-300" />
                  <p class="text-gray-500 text-lg font-medium">No lead forms found</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="forms.links && forms.links.length > 3" class="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
        <div class="text-sm text-gray-700">
          Showing {{ forms.from }} to {{ forms.to }} of {{ forms.total }} results
        </div>
        <div class="flex gap-2">
          <Link
            v-for="link in forms.links"
            :key="link.label"
            :href="link.url || '#'"
            v-html="link.label"
            :class="[
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              link.active
                ? 'bg-green-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300',
              !link.url ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
            ]"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Link, router } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon.vue'
import Layout from '@/Shared/Layout'

export default {
  layout: Layout,
  components: {
    Link,
    Icon,
  },
  props: {
    forms: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      default: 'Lead Forms',
    },
  },
  methods: {
    deleteForm(id) {
      if (confirm('Are you sure you want to delete this lead form?')) {
        router.delete(route('admin.lead-forms.destroy', id), {
          preserveScroll: true,
          onSuccess: () => {
            if (window.$toast) {
              window.$toast.success('Lead form deleted successfully')
            }
          },
        })
      }
    },
  },
}
</script>

<style scoped>
.admin-lead-forms-index {
  min-height: 100vh;
  background-color: #f9fafb;
}
</style>



